import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { useApp } from "@/context/AppContext";
import { Radio, ArrowLeft, MapPin } from "lucide-react";
import { LiveNowCard, useLiveNow } from "@/components/LiveNowWidget";
import { NearbyMiniMap, useNearbyMapPoints } from "@/components/NearbyMiniMap";
import { CardGridSkeleton } from "@/components/FeedSkeleton";

// Lot 557 — filtres rapides : Tous · Mes églises (+ prédicateurs suivis) · Près de moi · Les plus suivis.
const LiveFilters = ({ lives, filter, setFilter, coords, getPos }) => {
  const { t } = useApp();
  const mine = lives.filter((l) => l.mine).length;
  const near = lives.filter((l) => l._distance != null).length;
  const pills = [
    { key: "all", label: t("live.filter_all", "Tous"), count: lives.length },
    { key: "mine", label: t("live.filter_mine", "Mes églises"), count: mine },
    { key: "near", label: t("live.filter_near", "Près de moi"), count: coords ? near : null, icon: MapPin },
    { key: "top", label: t("live.filter_top", "Les plus suivis"), count: null },
  ];
  return (
    <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-1 px-1 pb-1" data-testid="lives-filters">
      {pills.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => { if (p.key === "near" && !coords) getPos?.(); setFilter(p.key); }}
          className={`pill shrink-0 !text-xs ${filter === p.key ? "pill-primary" : "pill-outline"}`}
          data-testid={`lives-filter-${p.key}`}
        >
          {p.icon && <p.icon size={12} className="mr-1" />}{p.label}{p.count != null ? ` (${p.count})` : ""}
        </button>
      ))}
    </div>
  );
};

// Dedicated "all lives right now" page — the feed card only features the most relevant live + bubbles.
export default function LiveNowPage() {
  const { t, config } = useApp();
  const nav = useNavigate();
  const { lives, tick, hasNearby, coords, permState, getPos, loading } = useLiveNow();
  const [filter, setFilter] = useState("all");
  const canEnableLoc = !coords && permState && permState !== "granted" && !!navigator.geolocation;
  const mapEnabled = !!config?.settings?.nearby_map_enabled;
  const mapPoints = useNearbyMapPoints(mapEnabled ? coords : null);
  const goToPoint = (p) => nav(p.type === "live" ? `/live?watch=${p.id}` : `/churches/${p.id}`);
  const shown = useMemo(() => {
    if (filter === "mine") return lives.filter((l) => l.mine);
    if (filter === "near") return lives.filter((l) => l._distance != null).sort((a, b) => a._distance - b._distance);
    if (filter === "top") return [...lives].sort((a, b) => (b.viewers || 0) - (a.viewers || 0));
    return lives;
  }, [lives, filter]);
  const emptyFilterHint = filter === "near" && !coords
    ? t("live.filter_near_hint", "Autorise ta position pour voir les directs près de toi.")
    : filter === "mine" ? t("live.filter_mine_hint", "Aucun direct de tes églises ou des prédicateurs que tu suis pour le moment.")
      : t("live.filter_empty", "Aucun direct dans cette sélection.");

  return (
    <AppShell hideRightRail>
      <div className="space-y-4">
        <div className="yalola-card p-5" data-testid="lives-page-hero">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm font-semibold mb-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="lives-page-back">
            <ArrowLeft size={16} /> {t("common.back", "Retour")}
          </button>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Radio size={22} style={{ color: "#e11d48" }} />
              <h1 className="text-2xl lg:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{hasNearby ? t("live.churches_near", "Directs près de toi") : t("live.now", "En direct")}</h1>
              {lives.length > 0 && <span className="rounded-full px-2 py-0.5 text-xs font-bold text-white" style={{ background: "#e11d48" }} data-testid="lives-page-count">{lives.length}</span>}
            </div>
            {canEnableLoc && (
              <button
                type="button"
                onClick={getPos}
                className="flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors"
                style={{ color: "var(--yalola-primary)", borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }}
                data-testid="lives-page-enable-location"
              >
                <MapPin size={13} /> {t("live.enable_location", "Voir les directs près de toi")}
              </button>
            )}
          </div>
          <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("live.now_subtitle", "Tous les directs en cours. Rejoins une communauté qui prie en ce moment 🔴")}</p>
          {lives.length > 1 && <div className="mt-3"><LiveFilters lives={lives} filter={filter} setFilter={setFilter} coords={coords} getPos={getPos} /></div>}
        </div>

        {mapEnabled && coords && mapPoints.length > 0 && (
          <div className="yalola-card p-2" data-testid="lives-map-card">
            <NearbyMiniMap center={coords} points={mapPoints} height={240} onPointClick={goToPoint} />
          </div>
        )}

        {loading && <CardGridSkeleton count={6} cols="grid-cols-2 md:grid-cols-3 lg:grid-cols-4" />}

        {!loading && !lives.length && (
          <div className="yalola-card p-8 text-center" data-testid="lives-page-empty">
            <div className="mx-auto mb-3 h-12 w-12 rounded-2xl grid place-items-center" style={{ background: "#e11d48" }}>
              <Radio size={22} className="text-white" />
            </div>
            <p className="font-semibold text-lg" style={{ fontFamily: "Outfit" }}>{t("live.none_now", "Aucun direct en cours")}</p>
            <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("live.none_now_hint", "Reviens bientôt — ou programme ton propre direct.")}</p>
          </div>
        )}

        {!loading && lives.length > 0 && !shown.length && (
          <div className="yalola-card p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="lives-page-filter-empty">{emptyFilterHint}</div>
        )}

        {!loading && shown.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="lives-page-list" data-filter={filter}>
            {shown.map((l) => (
              <LiveNowCard key={l.id} l={l} tick={tick} variant="grid" />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
